'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { stripInvisibles } from '@workspace/constants';
import { ProductDetailLevel3Data, ProductVariant } from '../../interfaces';

export interface UseProductDetailTemplateResult {
  selectedVariant: ProductVariant | null;
  /** True only when the current filters narrow down to exactly one variant. */
  isExactMatch: boolean;
  selectedAttributes: Record<string, string>;
  carouselKey: number;
  allImages: Array<{
    _key: string;
    alt?: string;
    asset: {
      _ref?: string;
      url: string;
    };
    hotspot?: unknown;
    crop?: unknown;
  }>;
  handleAttributeChange: (attributeId: string, value: string) => void;
  getAvailableValues: (attributeId: string) => string[];
  getAllPossibleValues: (attributeId: string) => string[];
}

export const useProductDetailTemplate = (product: ProductDetailLevel3Data): UseProductDetailTemplateResult => {
  const [selectedVariant, setSelectedVariant] = useState<ProductVariant | null>(null);
  const [matchingVariantsCount, setMatchingVariantsCount] = useState(0);
  const [selectedAttributes, setSelectedAttributes] = useState<Record<string, string>>({});
  const [carouselKey, setCarouselKey] = useState(0);

  // Reset carousel to first image when variant changes
  useEffect(() => {
    setCarouselKey((prev) => prev + 1);
  }, [selectedVariant]);

  const allImages = useMemo(() => {
    const images: UseProductDetailTemplateResult['allImages'] = [];

    const seen = new Set<string>();
    const pushUnique = (img?: UseProductDetailTemplateResult['allImages'][number]) => {
      if (!img) return;
      const id = img.asset._ref || img.asset.url;
      if (!id || seen.has(id)) return;
      seen.add(id);
      images.push(img);
    };

    if (selectedVariant?.variantImages && selectedVariant.variantImages.length > 0) {
      // Variant selected: 1) selected variant images, 2) primary, 3) other variants, 4) product images
      selectedVariant.variantImages.forEach((img, index) => {
        pushUnique({ ...img, _key: img._key || `selected-variant-${index}` });
      });

      if (product.primaryImage) {
        pushUnique({
          _key: 'primary',
          alt: product.primaryImage.alt,
          asset: product.primaryImage.asset,
          hotspot: product.primaryImage.hotspot,
          crop: product.primaryImage.crop,
        });
      }

      if (product.productVariants && product.productVariants.length > 0) {
        product.productVariants.forEach((variant, vIdx) => {
          if (!variant.variantImages || variant.variantImages.length === 0) return;
          variant.variantImages.forEach((img, iIdx) => {
            pushUnique({ ...img, _key: img._key || `variant-${vIdx}-${iIdx}` });
          });
        });
      }

      if (product.productImages && product.productImages.length > 0) {
        product.productImages.forEach((img, pIdx) => pushUnique({ ...img, _key: img._key || `product-${pIdx}` }));
      }
    } else {
      // No variant selected: 1) primary, 2) all variants, 3) product images
      if (product.primaryImage) {
        pushUnique({
          _key: 'primary',
          alt: product.primaryImage.alt,
          asset: product.primaryImage.asset,
          hotspot: product.primaryImage.hotspot,
          crop: product.primaryImage.crop,
        });
      }

      if (product.productVariants && product.productVariants.length > 0) {
        product.productVariants.forEach((variant, vIdx) => {
          if (!variant.variantImages || variant.variantImages.length === 0) return;
          variant.variantImages.forEach((img, iIdx) => {
            pushUnique({ ...img, _key: img._key || `variant-${vIdx}-${iIdx}` });
          });
        });
      }

      if (product.productImages && product.productImages.length > 0) {
        product.productImages.forEach((img, pIdx) => pushUnique({ ...img, _key: img._key || `product-${pIdx}` }));
      }
    }

    return images;
  }, [product.primaryImage, product.productImages, product.productVariants, selectedVariant]);

  const getAllPossibleValues = useCallback(
    (attributeId: string): string[] => {
      const values = new Set<string>();

      product.productVariants.forEach((variant) => {
        const attrValue = variant.attributeValues?.find((av) => av.attribute._ref === attributeId);
        if (attrValue && attrValue.value) {
          const cleanValue = stripInvisibles(attrValue.value).trim();
          if (cleanValue) {
            values.add(cleanValue);
          }
        }
      });

      return Array.from(values).sort((a, b) => {
        // Sort dimensions first
        const aDimensionMatch = a.match(/^(\d+(?:\.\d+)?)\s*x\s*(\d+(?:\.\d+)?)$/i);
        const bDimensionMatch = b.match(/^(\d+(?:\.\d+)?)\s*x\s*(\d+(?:\.\d+)?)$/i);

        if (aDimensionMatch && bDimensionMatch) {
          const aWidth = parseFloat(aDimensionMatch[1]);
          const aHeight = parseFloat(aDimensionMatch[2]);
          const bWidth = parseFloat(bDimensionMatch[1]);
          const bHeight = parseFloat(bDimensionMatch[2]);

          if (aWidth !== bWidth) {
            return aWidth - bWidth;
          }
          return aHeight - bHeight;
        }

        if (aDimensionMatch) return -1;

        if (bDimensionMatch) return 1;

        const aNum = parseFloat(a);
        const bNum = parseFloat(b);

        // Sort numbers second
        if (!isNaN(aNum) && !isNaN(bNum)) {
          return aNum - bNum;
        }
        if (!isNaN(aNum)) return -1;
        if (!isNaN(bNum)) return 1;

        // Sort text last
        return a.localeCompare(b, undefined, { sensitivity: 'base' });
      });
    },
    [product.productVariants],
  );

  const getAvailableValues = useCallback(
    (attributeId: string): string[] => {
      const values = new Set<string>();

      const allPossibleValues = getAllPossibleValues(attributeId);

      allPossibleValues.forEach((value) => {
        const hasCompatibleVariant = product.productVariants.some((variant) => {
          const variantAttrValue = variant.attributeValues.find((av) => av.attribute._ref === attributeId);
          if (!variantAttrValue || stripInvisibles(variantAttrValue.value).trim() !== value) {
            return false;
          }

          return Object.entries(selectedAttributes).every(([selectedAttrId, selectedValue]) => {
            if (selectedAttrId === attributeId) return true;
            if (!selectedValue) return true;

            const otherVariantAttrValue = variant.attributeValues.find((av) => av.attribute._ref === selectedAttrId);
            if (!otherVariantAttrValue) return false;

            return stripInvisibles(otherVariantAttrValue.value).trim() === selectedValue;
          });
        });

        if (hasCompatibleVariant) {
          values.add(value);
        }
      });

      return Array.from(values);
    },
    [getAllPossibleValues, product.productVariants, selectedAttributes],
  );

  const requiredAttributeIds = useMemo(() => {
    if (product.attributeFilters && product.attributeFilters.length > 0) {
      return product.attributeFilters.map((f) => f.attribute._ref);
    }
    const ids = new Set<string>();
    product.productVariants.forEach((v) => v.attributeValues.forEach((av) => ids.add(av.attribute._ref)));
    return Array.from(ids);
  }, [product.attributeFilters, product.productVariants]);

  const handleAttributeChange = useCallback(
    (attributeId: string, value: string) => {
      const newSelectedAttributes: Record<string, string> = {
        ...selectedAttributes,
        [attributeId]: value,
      };

      if (!value) {
        delete newSelectedAttributes[attributeId];
      }

      setSelectedAttributes(newSelectedAttributes);

      if (Object.keys(newSelectedAttributes).length === 0) {
        setSelectedVariant(null);
        setMatchingVariantsCount(0);
        return;
      }

      const allRequiredSelected = requiredAttributeIds.every((id) => Boolean(newSelectedAttributes[id]));

      let matchingVariant: ProductVariant | null = null;
      let count = 0;
      if (allRequiredSelected) {
        const exactMatches = product.productVariants.filter((variant) => {
          return requiredAttributeIds.every((attrId) => {
            const selectedValue = newSelectedAttributes[attrId];
            const variantAttrValue = variant.attributeValues.find((av) => av.attribute._ref === attrId);
            return variantAttrValue && stripInvisibles(variantAttrValue.value).trim() === selectedValue;
          });
        });
        count = exactMatches.length;
        matchingVariant = exactMatches[0] ?? null;
      } else {
        const candidates = product.productVariants.filter((variant) => {
          return Object.entries(newSelectedAttributes).every(([attrId, selectedValue]) => {
            if (!selectedValue) return true;
            const variantAttrValue = variant.attributeValues.find((av) => av.attribute._ref === attrId);
            return variantAttrValue && stripInvisibles(variantAttrValue.value).trim() === selectedValue;
          });
        });
        count = candidates.length;
        matchingVariant = candidates.length > 0 ? candidates[0] : null;
      }

      setMatchingVariantsCount(count);
      setSelectedVariant(matchingVariant);
    },
    [product.productVariants, requiredAttributeIds, selectedAttributes],
  );

  return {
    selectedVariant,
    isExactMatch: matchingVariantsCount === 1,
    selectedAttributes,
    carouselKey,
    allImages,
    handleAttributeChange,
    getAvailableValues,
    getAllPossibleValues,
  };
};
